import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import {
  GroupSubMenu,
  GroupSubMenuDto,
  GroupSubMenuWithMenuCount,
} from '../../../../core/models/system-settings/group-sub-menus.model';
import { MenuList, MenuListDto } from '../../../../core/models/system-settings/menu-lists.model';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperGroupSubMenusService } from '../../../../core/mappers/system-settings/mapper-group-sub-menus.service';
import { MapperMenuListsService } from '../../../../core/mappers/system-settings/mapper-menu-lists.service';
import { MapperUserAccessMenuService } from '../../../../core/mappers/mapper-user-menu.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalGroupSubMenuComponent } from '../../modals/system-settings/modal-group-sub-menu/modal-group-sub-menu.component';
import { ModalMenuListComponent } from '../../modals/system-settings/modal-menu-list/modal-menu-list.component';
import { forkJoin, lastValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-group-sub-menus',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalGroupSubMenuComponent,
    ModalMenuListComponent,
  ],
  templateUrl: './group-sub-menus.component.html',
  styleUrl: './group-sub-menus.component.css'
})
export class GroupSubMenusComponent implements OnInit {
  private apiService = inject(ApiSystemSettingsService);
  private groupSubMapper = inject(MapperGroupSubMenusService);
  private menuListMapper = inject(MapperMenuListsService);
  private userMenuMapper = inject(MapperUserAccessMenuService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private permissionService = inject(MenuPermissionService);
  private readonly menuPathById = new Map(
    this.userMenuMapper.getMenuPathOptions().map(option => [option.pathId, option.path])
  );

  groupSubsWithCount = signal<GroupSubMenuWithMenuCount[]>([]);
  menuLists = signal<MenuList[]>([]);
  selectedGroupSub = signal<GroupSubMenuWithMenuCount | null>(null);

  isLoading = signal<boolean>(false);
  isDashboardOpen = signal<boolean>(true);
  subSearchText = signal<string>('');
  menuSearchText = signal<string>('');

  isSubModalOpen = signal<boolean>(false);
  selectedSubItem = signal<GroupSubMenu | null>(null);

  isMenuModalOpen = signal<boolean>(false);
  selectedMenuItem = signal<MenuList | null>(null);

  isDeleteSubModalOpen = signal<boolean>(false);
  subToDelete = signal<GroupSubMenuWithMenuCount | null>(null);

  isDeleteMenuModalOpen = signal<boolean>(false);
  menuToDelete = signal<MenuList | null>(null);

  // Import / Export State
  isExporting = signal<boolean>(false);
  isImportModalOpen = signal<boolean>(false);
  importMode = signal<'skip' | 'update' | 'force'>('skip');
  pendingImportData = signal<any>(null);
  selectedFileName = signal<string>('');
  importSummary = signal<{ groupMenusCount: number; groupSubMenusCount: number; menuListsCount: number }>({
    groupMenusCount: 0,
    groupSubMenusCount: 0,
    menuListsCount: 0
  });
  importProgress = signal<{ current: number; total: number; text: string }>({
    current: 0,
    total: 0,
    text: ''
  });

  dashboardStats = signal<any[]>([]);

  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  pageSizeOptions = [20, 50, 100];
  sortColumn = signal<string>('number');
  sortDirection = signal<'asc' | 'desc'>('asc');

  Math = Math;

  filteredGroupSubs = computed(() => {
    const search = this.subSearchText().trim().toLowerCase();
    let list = this.groupSubsWithCount();
    if (search) {
      list = list.filter(s =>
        s.name.toLowerCase().includes(search) ||
        s.groupMenuName.toLowerCase().includes(search) ||
        s.id.toString() === search
      );
    }
    return [...list].sort((a, b) => {
      const menuCmp = a.groupMenuName.localeCompare(b.groupMenuName, 'th');
      if (menuCmp !== 0) return menuCmp;
      return a.number - b.number;
    });
  });

  groupedSubs = computed(() => {
    const map = new Map<string, GroupSubMenuWithMenuCount[]>();
    for (const sub of this.filteredGroupSubs()) {
      const key = sub.groupMenuName || 'อื่นๆ';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(sub);
    }
    return Array.from(map.entries()).map(([groupMenuName, items]) => ({ groupMenuName, items }));
  });

  filteredMenuData = computed(() => {
    let filtered = this.menuLists();
    const search = this.menuSearchText().trim().toLowerCase();

    if (search) {
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(search) ||
        m.path.toLowerCase().includes(search) ||
        m.id.toString() === search
      );
    }

    const col = this.sortColumn();
    const dir = this.sortDirection();
    if (col) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[col];
        let valB = b[col];
        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA === null || valA === undefined) valA = '';
        if (valB === null || valB === undefined) valB = '';
        if (valA < valB) return dir === 'asc' ? -1 : 1;
        if (valA > valB) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  });

  totalItems = computed(() => this.filteredMenuData().length);

  filteredAndPaginatedMenuData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredMenuData().slice(start, start + this.pageSize());
  });

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get isSubPickerView(): boolean {
    return this.selectedGroupSub() === null;
  }

  get isMenuTableView(): boolean {
    return this.selectedGroupSub() !== null;
  }

  ngOnInit() {
    this.loadSubPickerStep();
  }

  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  onRefresh(): void {
    if (this.isSubPickerView) {
      this.subSearchText.set('');
      this.loadSubPickerStep();
    } else {
      this.menuSearchText.set('');
      this.currentPage.set(1);
      this.reloadMenuTable();
    }
  }

  selectGroupSub(sub: GroupSubMenuWithMenuCount): void {
    this.selectedGroupSub.set(sub);
    this.menuSearchText.set('');
    this.currentPage.set(1);
    this.reloadMenuTable();
  }

  backToSubPicker(): void {
    this.selectedGroupSub.set(null);
    this.menuLists.set([]);
    this.menuSearchText.set('');
    this.loadSubPickerStep();
  }

  navigateToCreateSub(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedSubItem.set(null);
    this.isSubModalOpen.set(true);
  }

  navigateToEditSub(sub: GroupSubMenuWithMenuCount, event?: Event): void {
    event?.stopPropagation();
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedSubItem.set({
      id: sub.id,
      name: sub.name,
      enable: sub.enable,
      groupMenuId: sub.groupMenuId,
      groupMenuName: sub.groupMenuName,
      number: sub.number,
      icon: sub.icon,
      createDate: null,
      writeDate: null,
    });
    this.isSubModalOpen.set(true);
  }

  onDeleteSub(sub: GroupSubMenuWithMenuCount, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    this.subToDelete.set(sub);
    this.isDeleteSubModalOpen.set(true);
  }

  closeSubModal(): void {
    this.isSubModalOpen.set(false);
    this.selectedSubItem.set(null);
  }

  closeDeleteSubModal(): void {
    this.isDeleteSubModalOpen.set(false);
    this.subToDelete.set(null);
  }

  confirmDeleteSub(): void {
    const sub = this.subToDelete();
    if (!sub) return;

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;

    this.isLoading.set(true);
    this.closeDeleteSubModal();

    this.apiService.deleteGroupSubMenu(sub.id, userId, menuId).subscribe({
      next: (res: any) => {
        console.log('deleteGroupSubMenu : ', res);
        if (res?.isSuccess) {
          this.toast.success('ลบกลุ่มเมนูย่อยสำเร็จ');
          this.onRefresh();
        } else {
          this.toast.error(res?.message || 'ลบกลุ่มเมนูย่อยไม่สำเร็จ');
        }
        this.isLoading.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ลบกลุ่มเมนูย่อยไม่สำเร็จ');
        this.isLoading.set(false);
      }
    });
  }

  navigateToCreateMenu(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedMenuItem.set(null);
    this.isMenuModalOpen.set(true);
  }

  navigateToEditMenu(item: MenuList): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedMenuItem.set(item);
    this.isMenuModalOpen.set(true);
  }

  onDeleteMenu(item: MenuList): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    this.menuToDelete.set(item);
    this.isDeleteMenuModalOpen.set(true);
  }

  closeMenuModal(): void {
    this.isMenuModalOpen.set(false);
    this.selectedMenuItem.set(null);
  }

  closeDeleteMenuModal(): void {
    this.isDeleteMenuModalOpen.set(false);
    this.menuToDelete.set(null);
  }

  confirmDeleteMenu(): void {
    const item = this.menuToDelete();
    if (!item) return;

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;

    this.isLoading.set(true);
    this.closeDeleteMenuModal();
    console.log('deleteMenuList : ', JSON.stringify(item));
    console.log('userId : ', userId);
    console.log('menuId : ', menuId);
    console.log('item.id : ', item.id);
    this.apiService.deleteMenuList(item.id, userId, menuId).subscribe({
      next: (res: any) => {
        console.log('deleteMenuList : ', res);
        if (res?.isSuccess) {
          this.toast.success('ลบรายการเมนูสำเร็จ');
          this.onRefresh();
        } else {
          this.toast.error(res?.message || 'ลบรายการเมนูไม่สำเร็จ');
        }
        this.isLoading.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ลบรายการเมนูไม่สำเร็จ');
        this.isLoading.set(false);
      }
    });
  }

  onSearchMenus(): void {
    this.currentPage.set(1);
  }

  sortData(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  get menuPresetContext() {
    const sub = this.selectedGroupSub();
    if (!sub) return null;
    return {
      groupMenuId: sub.groupMenuId,
      groupMenuName: sub.groupMenuName,
      groupSubId: sub.id,
      groupSubName: sub.name,
    };
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / (this.pageSize() || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
  }

  trackById(index: number, item: { id: number }): number {
    return item.id;
  }

  private calculateDashboardStats(subs: GroupSubMenuWithMenuCount[]): void {
    const total = subs.length;
    const activeTotal = subs.filter(s => s.enable).length;
    const totalMenus = subs.reduce((sum, s) => sum + s.menuCount, 0);

    this.dashboardStats.set([
      { title: 'กลุ่มเมนูย่อยทั้งหมด', value: total.toLocaleString(), icon: 'class', color: 'text-amber-500' },
      { title: 'เปิดใช้งาน', value: activeTotal.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
      { title: 'รายการเมนูทั้งหมด', value: totalMenus.toLocaleString(), icon: 'menu_book', color: 'text-sky-500' },
    ]);
  }

  private loadSubPickerStep(): void {
    this.isLoading.set(true);

    this.apiService.getGroupSubMenusForTable().subscribe({
      next: (subDtos: GroupSubMenuDto[]) => {
        const subs = (subDtos || []).map(d => this.groupSubMapper.mapGroupSubMenuFromDto(d));

        this.apiService.getMenuListsForTable().subscribe({
          next: (menuDtos: MenuListDto[]) => {
            const counts: Record<number, number> = {};
            (menuDtos || []).forEach(m => {
              if (m.groupSubId) {
                counts[m.groupSubId] = (counts[m.groupSubId] || 0) + 1;
              }
            });

            const withCount: GroupSubMenuWithMenuCount[] = subs.map(s => ({
              id: s.id,
              name: s.name,
              enable: s.enable,
              groupMenuId: s.groupMenuId,
              groupMenuName: s.groupMenuName,
              number: s.number,
              icon: s.icon,
              menuCount: counts[s.id] || 0,
            }));

            this.groupSubsWithCount.set(withCount);
            this.calculateDashboardStats(withCount);
            this.isLoading.set(false);
          },
          error: () => {
            const withCount: GroupSubMenuWithMenuCount[] = subs.map(s => ({
              id: s.id,
              name: s.name,
              enable: s.enable,
              groupMenuId: s.groupMenuId,
              groupMenuName: s.groupMenuName,
              number: s.number,
              icon: s.icon,
              menuCount: 0,
            }));
            this.groupSubsWithCount.set(withCount);
            this.calculateDashboardStats(withCount);
            this.isLoading.set(false);
          }
        });
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูลกลุ่มเมนูย่อยได้');
        this.isLoading.set(false);
      }
    });
  }

  private reloadMenuTable(): void {
    const sub = this.selectedGroupSub();
    if (!sub) return;

    this.isLoading.set(true);
    this.apiService.getMenuListsForTable(sub.groupMenuId, sub.id).subscribe({
      next: (dtos: MenuListDto[]) => {
        console.log('getMenuListsForTable : ', dtos);
        try {
          const list = (dtos || []).map(d => {
            const menu = this.menuListMapper.mapMenuListFromDto(d);
            return {
              ...menu,
              path: menu.pathId ? this.menuPathById.get(menu.pathId) ?? '' : '',
            };
          });
          this.menuLists.set(list);
        } catch {
          this.toast.error('โครงสร้างข้อมูลไม่ถูกต้อง');
        } finally {
          this.isLoading.set(false);
        }
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดรายการเมนูได้');
        this.isLoading.set(false);
      }
    });
  }

  // ================= Import / Export Functions =================

  /**
   * Reads and parses uploaded JSON file for menu structure import.
   * @param event HTML File Input event
   */
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.selectedFileName.set(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = JSON.parse(text);

        let hierarchy: any[] = [];
        if (parsed.hierarchy && Array.isArray(parsed.hierarchy)) {
          hierarchy = parsed.hierarchy;
        } else if (parsed.groupMenus && Array.isArray(parsed.groupMenus)) {
          hierarchy = parsed.groupMenus;
        } else if (Array.isArray(parsed)) {
          hierarchy = parsed;
        }

        if (hierarchy.length === 0) {
          this.toast.error('ไม่พบโครงสร้างข้อมูลเมนูในไฟล์ JSON');
          input.value = '';
          return;
        }

        let gCount = hierarchy.length;
        let subCount = 0;
        let menuCount = 0;

        hierarchy.forEach((g: any) => {
          if (g.subMenus && Array.isArray(g.subMenus)) {
            subCount += g.subMenus.length;
            g.subMenus.forEach((s: any) => {
              if (s.menuLists && Array.isArray(s.menuLists)) {
                menuCount += s.menuLists.length;
              }
            });
          }
          if (g.menuLists && Array.isArray(g.menuLists)) {
            menuCount += g.menuLists.length;
          }
        });

        this.pendingImportData.set({ hierarchy, original: parsed });
        this.importSummary.set({
          groupMenusCount: gCount,
          groupSubMenusCount: subCount,
          menuListsCount: menuCount
        });
        this.isImportModalOpen.set(true);
      } catch (err) {
        console.error(err);
        this.toast.error('ไฟล์ JSON ไม่ถูกต้อง กรุณาตรวจสอบรูปแบบไฟล์');
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  }

  /**
   * Closes the menu import modal.
   */
  public closeImportModal(): void {
    this.isImportModalOpen.set(false);
    this.pendingImportData.set(null);
    this.selectedFileName.set('');
    this.importProgress.set({ current: 0, total: 0, text: '' });
  }

  /**
   * Exports full menu structure (Group Menus, Group Sub Menus, Menu Lists) as a JSON file.
   */
  public exportMenuData(): void {
    this.isExporting.set(true);
    this.isLoading.set(true);

    forkJoin([
      this.apiService.getGroupMenusForTable().pipe(catchError(() => of([]))),
      this.apiService.getGroupSubMenusForTable().pipe(catchError(() => of([]))),
      this.apiService.getMenuListsForTable().pipe(catchError(() => of([])))
    ]).subscribe({
      next: ([groupDtos, subDtos, menuDtos]) => {
        const groupMap = new Map<number, any>();
        (groupDtos || []).forEach(g => {
          groupMap.set(g.id, {
            name: g.name,
            status: g.status ?? true,
            number: g.number || 1,
            subMenus: [],
            menuLists: []
          });
        });

        const subMap = new Map<number, any>();
        (subDtos || []).forEach(s => {
          const subObj = {
            name: s.name,
            enable: s.enable ?? true,
            number: s.number || 1,
            icon: s.icon || 'folder',
            menuLists: []
          };
          subMap.set(s.id, subObj);

          if (s.groupMenuId && groupMap.has(s.groupMenuId)) {
            groupMap.get(s.groupMenuId).subMenus.push(subObj);
          }
        });

        (menuDtos || []).forEach(m => {
          const menuObj = {
            name: m.name,
            status: m.status ?? true,
            path: m.path || '',
            pathId: m.pathId ?? null,
            number: m.number || 1
          };

          if (m.groupSubId && subMap.has(m.groupSubId)) {
            subMap.get(m.groupSubId).menuLists.push(menuObj);
          } else if (m.groupMenuId && groupMap.has(m.groupMenuId)) {
            groupMap.get(m.groupMenuId).menuLists.push(menuObj);
          }
        });

        const hierarchy = Array.from(groupMap.values());

        const exportData = {
          exportVersion: '1.0',
          exportedAt: new Date().toISOString(),
          summary: {
            groupMenusCount: groupDtos.length,
            groupSubMenusCount: subDtos.length,
            menuListsCount: menuDtos.length
          },
          hierarchy: hierarchy,
          raw: {
            groupMenus: groupDtos,
            groupSubMenus: subDtos,
            menuLists: menuDtos
          }
        };

        const jsonStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const now = new Date();
        const dateStr = now.toISOString().replace(/[-:T.]/g, '').slice(0, 14);

        a.href = url;
        a.download = `city_parking_menus_export_${dateStr}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.toast.success('ส่งออกข้อมูลเมนูสำเร็จแล้ว');
        this.isLoading.set(false);
        this.isExporting.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('เกิดข้อผิดพลาดในการดึงข้อมูลเมนูเพื่อส่งออก');
        this.isLoading.set(false);
        this.isExporting.set(false);
      }
    });
  }

  /**
   * Imports menu hierarchy via single API upsert (pathId / name). Does not touch roles.
   */
  public async importMenuData(): Promise<void> {
    const importPayload = this.pendingImportData();
    if (!importPayload || !importPayload.hierarchy) return;

    this.isLoading.set(true);

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;

    if (userId <= 0 || menuId <= 0) {
      this.toast.error('ไม่พบ userId หรือ menuId สำหรับตรวจสิทธิ์');
      this.isLoading.set(false);
      return;
    }

    const hierarchy: any[] = importPayload.hierarchy;
    this.importProgress.set({ current: 0, total: 1, text: 'กำลังนำเข้าโครงสร้างเมนู...' });

    try {
      const res: any = await lastValueFrom(
        this.apiService.importMenuStructure({
          mode: this.importMode(),
          userId,
          menuId,
          hierarchy,
        })
      );

      this.importProgress.set({ current: 1, total: 1, text: 'นำเข้าเสร็จแล้ว' });

      if (res?.isSuccess) {
        const created = res.menusCreated ?? 0;
        const updated = res.menusUpdated ?? 0;
        const skipped = res.menusSkipped ?? 0;
        this.toast.success(
          res.message || `นำเข้าสำเร็จ (สร้าง ${created}, อัปเดต ${updated}, ข้าม ${skipped})`
        );
        if (Array.isArray(res.warnings) && res.warnings.length > 0) {
          console.warn('importMenuStructure warnings:', res.warnings);
        }
        this.closeImportModal();
        this.onRefresh();
      } else {
        this.toast.error(res?.message || 'นำเข้าข้อมูลเมนูไม่สำเร็จ');
      }
    } catch (err) {
      console.error(err);
      this.toast.error('เกิดข้อผิดพลาดในการนำเข้าข้อมูลเมนู');
    } finally {
      this.isLoading.set(false);
    }
  }
}
